import React, { useState } from 'react';
import { ArrowRight, Zap } from 'lucide-react';
import { useAppStore } from '@/store';
import { selectCourseBudget, selectRecoveryPath, selectAbsenceCounts, selectTermProgress, selectTrajectory } from '@/store/selectors';
import { computeEffectiveAbsences } from '@abs/shared-logic';
import { BudgetGauge, RiskBadge, PenaltyCountdown, CollapsiblePanel, EmptyState, Button } from '../shared';
import { QuickLogModal } from '../QuickLog/QuickLogModal';
import { SessionTimeline } from '../SessionTimeline/SessionTimeline';
import { AttendanceSparkline } from '../AttendanceSparkline/AttendanceSparkline';
import { ThresholdTimeline } from '../ThresholdTimeline/ThresholdTimeline';
import { RiskLevel } from '@abs/shared-types';

export interface OverviewPanelProps {
  courseId: string;
}

/**
 * Switch the course-detail sidebar to the "Log Absence" item.
 *
 * SidebarNav (calc-sidebar) syncs its active item to the URL hash and
 * listens for `popstate`. `pushState` alone won't reach it, so we dispatch
 * a synthetic PopStateEvent to trigger the same handler the browser's
 * back/forward buttons use. This keeps navigation in the view layer — no
 * store or engine changes required.
 */
function goToLogAbsence(): void {
  if (typeof window === 'undefined') return;
  const hash = '#course-detail/log';
  if (window.location.hash === hash) return;
  window.history.pushState(null, '', hash);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function OverviewPanel({ courseId }: OverviewPanelProps): React.ReactElement {
  const budget = useAppStore((s) => selectCourseBudget(s, courseId));
  const recovery = useAppStore((s) => selectRecoveryPath(s, courseId));
  const counts = useAppStore((s) => selectAbsenceCounts(s, courseId));
  const termProgress = useAppStore((s) => selectTermProgress(s, courseId));
  const trajectory = useAppStore((s) => selectTrajectory(s, courseId));
  const course = useAppStore((s) => s.courses.find((c) => c.id === courseId));
  const policy = useAppStore((s) => s.policies.find((p) => p.id === course?.policyId));
  const entries = useAppStore((s) => s.entries.filter((e) => e.courseId === courseId));
  const [quickLogOpen, setQuickLogOpen] = useState(false);

  // True empty state: no policy attached to the course, so no budget can be
  // computed. Orient the user toward setup rather than showing a bare "No
  // data" dead-end.
  if (!budget) {
    return (
      <EmptyState
        title="No attendance policy yet"
        description="Assign an attendance policy to this course. Once set, your absence budget, gauge, and recovery path will appear here."
      />
    );
  }

  // CSS-safe risk modifier: enum uses 'at_risk', classes use 'at-risk'.
  const riskModifier = budget.riskLevel.replace('_', '-');
  const isCritical = budget.riskLevel === RiskLevel.Critical;

  // First-use onboarding beat: a policy is configured but no sessions have
  // been logged yet. This is the moment of greatest uncertainty ("did I set
  // this up right?"), so instead of a dead-end empty state we render the
  // same status hero the user will live in — at 0/N — with an explicit
  // allowance headline and a single clear next action. Reads as the start
  // of the populated product, not a separate empty-state branch.
  const isFirstUse = entries.length === 0 && budget.usedTotal === 0;
  if (isFirstUse) {
    const allowanceWord = budget.totalAllowed === 1 ? 'class' : 'classes';
    return (
      <div className="overview-panel" aria-live="polite">
        <section
          className={`overview-hero overview-hero--${riskModifier} overview-hero--first-use${isCritical ? ' overview-hero--critical-loud' : ''}`}
          aria-label="Absence budget status"
        >
          <div className="overview-hero__masthead">
            <RiskBadge level={budget.riskLevel} size="lg" />
            <p className="overview-hero__first-use-kicker">Policy set — ready to track</p>
          </div>

          <p className="overview-hero__headline overview-hero__headline--onboarding">
            <span className="overview-hero__number">{budget.totalAllowed}</span>
            <span className="overview-hero__unit">
              {allowanceWord} you can miss
            </span>
            <span className="overview-hero__headline-sub">
              before penalties begin under your current policy. Log your first session to start counting against this allowance.
            </span>
          </p>

          <BudgetGauge
            used={0}
            total={budget.totalAllowed}
            riskLevel={budget.riskLevel}
            label={`0 of ${budget.totalAllowed} used`}
          />

          <div className="overview-hero__first-use-cta">
            <Button variant="primary" size="lg" onClick={goToLogAbsence}>
              Log your first session
              <ArrowRight size={18} aria-hidden="true" />
            </Button>
            <p className="overview-hero__first-use-hint">
              As you log sessions, your remaining budget, risk level, and recovery path will live here.
            </p>
          </div>
        </section>
      </div>
    );
  }

  // Tardy conversion countdown (TODO-04)
  const tardyEff = policy ? computeEffectiveAbsences(entries, policy) : null;
  const showTardyCountdown =
    !!policy?.tardyConversion?.enabled && policy.tardyConversion.tardiesPerAbsence > 0 && !!tardyEff;

  return (
    <div className="overview-panel" aria-live="polite">
      {/* ── Status Hero ─────────────────────────────────────────── */}
      {/* The one zone that answers "am I okay?". Tinted by risk level so
          urgency is felt before it is read. Critical gets a loud border
          treatment in addition to the fill. */}
      <section
        className={`overview-hero overview-hero--${riskModifier}${isCritical ? ' overview-hero--critical-loud' : ''}`}
        aria-label="Absence budget status"
      >
        <div className="overview-hero__masthead">
          <RiskBadge level={budget.riskLevel} size="lg" />
          {termProgress && (
            <p className={`stat-caption overview-hero__burn${termProgress.status === 'burning-fast' ? ' overview-hero__burn--fast' : ''}`}>
              Week {termProgress.weeksElapsed} of {termProgress.weeksTotal} — burning budget {termProgress.burnRatio.toFixed(1)}× faster than calendar time.
            </p>
          )}
          <Button
            variant="primary"
            size="sm"
            className="overview-hero__quick-log"
            onClick={() => setQuickLogOpen(true)}
            aria-label="Quick log today's absence"
          >
            <Zap size={16} aria-hidden="true" />
            Quick Log
          </Button>
        </div>

        <p className="overview-hero__headline">
          <span className="overview-hero__number">{budget.remaining}</span>
          <span className="overview-hero__unit">
            {budget.remaining === 1 ? 'absence' : 'absences'} remaining
          </span>
          <span className="overview-hero__headline-sub">
            of {budget.totalAllowed} allowed before auto-fail.
          </span>
        </p>

        <BudgetGauge
          used={budget.usedTotal}
          total={budget.totalAllowed}
          riskLevel={budget.riskLevel}
          label={`${budget.usedTotal} of ${budget.totalAllowed} used`}
        />

        <div className="overview-hero__stats">
          <div className="overview-hero__stat">
            <span className="overview-hero__stat-value">{budget.attendancePercentage.toFixed(1)}%</span>
            <span className="overview-hero__stat-label">Attendance</span>
          </div>
          <PenaltyCountdown
            absencesToNextTier={budget.absencesToNextTier}
            nextTier={budget.nextTier}
          />
        </div>

        {recovery && (
          <div className={`overview-hero__recovery${recovery.isRecoverable ? '' : ' overview-hero__recovery--critical'}`}>
            {recovery.sessionsNeeded === 0 ? (
              <span>You are in the safe zone!</span>
            ) : (
              <span>
                Attend the next <strong>{recovery.sessionsNeeded}</strong> session{recovery.sessionsNeeded > 1 ? 's' : ''} to return to safe zone.
                {recovery.isRecoverable
                  ? ` (Projected: ${recovery.projectedPercentage.toFixed(1)}%)`
                  : ' Recovery is not mathematically possible.'}
              </span>
            )}
          </div>
        )}
      </section>

      {/* ── Visual break: urgency above, trivia below ──────────── */}
      <div className="overview-panel__divider" role="separator" aria-hidden="true" />

      <div className="overview-panel__details">
        <div className="overview-panel__breakdown">
          <h4>Absence Breakdown</h4>
          <ul>
            <li>Unexcused: {counts.unexcused}</li>
            <li>Excused: {counts.excused}</li>
            <li>Late: {counts.late}</li>
            <li>Early Departure: {counts.earlyDepartures}</li>
            <li title="Full penalty absences generated from accumulated tardies, per the policy's conversion rule.">Tardy Conversions: {budget.usedTardyConversions}</li>
            {showTardyCountdown && tardyEff && (
              <li>
                <strong>{policy!.tardyConversion.tardiesPerAbsence - tardyEff.remainingTardies}</strong> / {policy!.tardyConversion.tardiesPerAbsence} tardies toward your next absence.
              </li>
            )}
            <li>Present: {counts.present}</li>
          </ul>
        </div>

        {budget.activeTier && (
          <CollapsiblePanel title="Active Penalty Details">
            <p><strong>Active Tier:</strong> {budget.activeTier.description}</p>
            <p><strong>Grade Deduction:</strong> {budget.activeTier.gradeDeduction} points</p>
            <p><strong>Letter Grade Drop:</strong> {budget.activeTier.letterGradeDrop}</p>
          </CollapsiblePanel>
        )}

        {course && (
          <CollapsiblePanel title="Session Timeline" defaultOpen={false}>
            <SessionTimeline course={course} entries={entries} />
          </CollapsiblePanel>
        )}

        {course && (
          <CollapsiblePanel title="Attendance Trend" defaultOpen={false}>
            <AttendanceSparkline course={course} entries={entries} />
          </CollapsiblePanel>
        )}

        {course && policy && (
          <CollapsiblePanel title="Threshold Projection" defaultOpen={false}>
            <ThresholdTimeline course={course} policy={policy} entries={entries} />
            {trajectory && (
              <div className="overview-panel__trajectory">
                <p className="stat-caption">
                  {trajectory.autoFailDate
                    ? `At current pace, you'll reach the auto-fail floor on ${trajectory.autoFailDate}.`
                    : "At your current pace, you won't reach the auto-fail floor."}
                </p>
                {trajectory.projectedFinalGrade !== null && trajectory.projectedFinalPoints !== null && (
                  <p className="stat-caption">
                    Projected final grade: {trajectory.projectedFinalGrade} ({trajectory.projectedFinalPoints.toFixed(1)}%).
                  </p>
                )}
              </div>
            )}
          </CollapsiblePanel>
        )}
      </div>

      {quickLogOpen && (
        <QuickLogModal isOpen={quickLogOpen} onClose={() => setQuickLogOpen(false)} />
      )}
    </div>
  );
}
